import React, { useEffect, useState } from 'react';
import {
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    message,
} from 'antd';
import {
    ApiOutlined,
    CloudSyncOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import StatusTag, { statusLabel } from '../../components/business/StatusTag';
import { MailAccount, MailboxMonitor } from '../../models/mail';
import { PageResult } from '../../models/v2';
import { v2Api } from '../../services/v2';
import { asV2Error } from '../../utils/v2';
import { mailFieldLabel } from './mailUi';
import { MAIL_PAGE_SIZE_OPTIONS, readMailPageSize, rememberMailPageSize } from './mailPagination';

const empty: PageResult<MailAccount> = {
    items: [], pageNo: 1, pageSize: readMailPageSize('accounts', 20), total: 0,
};

const MailAccountsPage = () => {
    const [form] = Form.useForm();
    const [data, setData] = useState(empty);
    const [editing, setEditing] = useState<MailAccount | null>(null);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [transport, setTransport] = useState('SMTP');
    const [inboundMonitor, setInboundMonitor] = useState<MailboxMonitor | null>(null);

    const load = async (pageNo = data.pageNo, pageSize = data.pageSize) => {
        setLoading(true);
        try {
            setData(await v2Api.mailAccounts({ pageNo, pageSize }));
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const loadInitial = async () => {
            setLoading(true);
            try {
                setData(await v2Api.mailAccounts({ pageNo: 1, pageSize: empty.pageSize }));
            } catch (error) {
                message.error(asV2Error(error).message);
            } finally {
                setLoading(false);
            }
        };
        loadInitial();
    }, []);

    const open = async (row?: MailAccount) => {
        const next = row || null;
        setEditing(next);
        setInboundMonitor(null);
        setVisible(true);
        setTransport(next?.transportType || 'SMTP');
        form.resetFields();
        form.setFieldsValue({
            ...(next
                ? { ...next, accountCode: next.providerAccountRef, smtpPassword: undefined }
                : {
                      transportType: 'SMTP',
                      smtpPort: 465,
                      smtpSecurity: 'SSL_TLS',
                      dailyLimit: 500,
                      ratePerMinute: 30,
                      maxAdditionalRetries: 3,
                      quotaTimezone: 'Asia/Shanghai',
                      status: 'INACTIVE',
                      version: 0,
                  }),
            imapPort: 993,
            imapSecurity: 'SSL_TLS',
            imapUsername: next?.fromAddress,
            imapFolderRef: 'INBOX',
            imapPollIntervalSeconds: 180,
            imapMaxMessagesPerPoll: 200,
            imapInitialLookbackDays: 30,
            imapStatus: 'ACTIVE',
        });
        if (next && next.transportType === 'SMTP') {
            try {
                const monitor = await v2Api.mailboxMonitor(next.id);
                setInboundMonitor(monitor);
                form.setFieldsValue({
                    imapHost: monitor.imapHost,
                    imapPort: monitor.imapPort || 993,
                    imapSecurity: monitor.imapSecurity || 'SSL_TLS',
                    imapUsername: monitor.imapUsername,
                    imapPassword: undefined,
                    imapFolderRef: monitor.folderRef || 'INBOX',
                    imapPollIntervalSeconds: monitor.pollIntervalSeconds || 180,
                    imapMaxMessagesPerPoll: monitor.maxMessagesPerPoll || 200,
                    imapInitialLookbackDays: monitor.initialLookbackDays || 30,
                    imapStatus: monitor.status || 'ACTIVE',
                });
            } catch (error) {
                const detail = asV2Error(error);
                if (detail.code !== 'NOT_FOUND') message.warning(`IMAP 配置读取失败：${detail.message}`);
            }
        }
    };
    const save = async () => {
        const values = { ...(await form.validateFields()) };
        delete values.environmentCode;
        delete values.replyToAddress;
        const smtpPassword = values.smtpPassword;
        const imapConfig = {
            adapterType: 'IMAP',
            mailboxRef: values.fromAddress,
            folderRef: values.imapFolderRef,
            credentialPassword: values.imapPassword,
            imapHost: values.imapHost,
            imapPort: Number(values.imapPort),
            imapSecurity: values.imapSecurity,
            imapUsername: values.imapUsername,
            pollIntervalSeconds: Number(values.imapPollIntervalSeconds),
            maxMessagesPerPoll: Number(values.imapMaxMessagesPerPoll),
            initialLookbackDays: Number(values.imapInitialLookbackDays),
            status: values.imapStatus,
            version: inboundMonitor?.version,
        };
        [
            'smtpPassword',
            'imapHost',
            'imapPort',
            'imapSecurity',
            'imapUsername',
            'imapPassword',
            'imapFolderRef',
            'imapPollIntervalSeconds',
            'imapMaxMessagesPerPoll',
            'imapInitialLookbackDays',
            'imapStatus',
        ].forEach((field) => delete values[field]);
        const payload = {
            ...values,
            credentialPassword: smtpPassword,
            // 当前产品不开放独立 Reply-To，接口必填值始终跟随完整发件邮箱。
            replyToAddress: values.fromAddress,
        };
        setSaving(true);
        let accountId = editing?.id;
        let accountSaved = false;
        try {
            if (editing)
                await v2Api.updateMailAccount(editing.id, {
                    ...payload,
                    version: editing.version || 0,
                });
            else accountId = (await v2Api.createMailAccount({ ...payload, version: 0 })).id;
            accountSaved = true;
            if (transport === 'SMTP' && accountId) {
                await v2Api.updateMailboxMonitor(accountId, imapConfig);
            }
            message.success(
                transport === 'SMTP'
                    ? editing
                        ? '邮箱账号和 IMAP 收件配置已更新'
                        : '邮箱账号和 IMAP 收件配置已创建'
                    : editing
                    ? '邮箱账号已更新'
                    : '邮箱账号已创建'
            );
            setVisible(false);
            await load(1, data.pageSize);
        } catch (error) {
            const detail = asV2Error(error);
            message.error(
                accountSaved && transport === 'SMTP'
                    ? `发件账号已保存，但 IMAP 配置失败：${detail.message}。请刷新后重新编辑。`
                    : detail.message
            );
            if (accountSaved) {
                setVisible(false);
                await load(1, data.pageSize);
            }
        } finally {
            setSaving(false);
        }
    };
    const test = async (row: MailAccount) => {
        setSaving(true);
        try {
            const result = await v2Api.testMailAccountOutbound(row.id);
            message.success(result.message);
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };
    const receiveNow = async (row: MailAccount) => {
        setSaving(true);
        try {
            const result = await v2Api.runMailboxMonitor(row.id);
            message.success(`IMAP 连接成功，本次同步 ${result.processedCount || 0} 封邮件`);
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };
    const activate = async (row: MailAccount) => {
        setSaving(true);
        try {
            const result = await v2Api.verifyAndActivateMailAccount(row.id);
            const checks = result.verificationChecks || {};
            const warnings: string[] = result.verificationWarnings || [];
            const checkRows = [
                ['SMTP', checks.smtp],
                ['SPF', checks.spf],
                ['DKIM', checks.dkim],
                ['DMARC', checks.dmarc],
            ];
            const statusColor = (status: string) => {
                if (status === 'PASS' || status === 'PASS_AUTO') return 'green';
                if (status === 'FAIL') return 'red';
                return 'orange';
            };
            const showResult =
                result.verificationStatus === 'PASS' && warnings.length === 0
                    ? Modal.success
                    : Modal.warning;
            showResult({
                title:
                    result.verificationStatus === 'PASS'
                        ? warnings.length
                            ? '账号已启用，但存在投递质量提醒'
                            : '账号验证通过并已启用'
                        : '账号验证未通过',
                width: 560,
                okText: '知道了',
                content: (
                    <div>
                        {checkRows.map(([name, check]: any) => (
                            <div key={name} style={{ marginTop: 10 }}>
                                <span style={{ display: 'inline-block', width: 90 }}>{name}</span>
                                <Tag color={statusColor(check?.status)}>
                                    {statusLabel(check?.status || 'UNKNOWN')}
                                </Tag>
                                <span>
                                    {check?.required ? '阻断项' : '建议项'} · {check?.message}
                                </span>
                            </div>
                        ))}
                        {warnings.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                                {warnings.map((warning) => (
                                    <div key={warning}>• {warning}</div>
                                ))}
                            </div>
                        )}
                    </div>
                ),
            });
            await load();
        } catch (error) {
            message.error(asV2Error(error).message);
        } finally {
            setSaving(false);
        }
    };
    const columns = [
        {
            title: '账号',
            render: (_: any, r: MailAccount) => (
                <div>
                    <strong>{r.providerAccountRef}</strong>
                    <span className="v2-subline">{r.fromAddress}</span>
                </div>
            ),
        },
        {
            title: 'SMTP',
            width: 210,
            render: (_: any, r: MailAccount) =>
                r.smtpHost ? `${r.smtpHost}:${r.smtpPort} · ${mailFieldLabel(r.smtpSecurity)}` : '—',
        },
        {
            title: '验证',
            dataIndex: 'verificationStatus',
            width: 160,
            render: (v: string, row: MailAccount) => {
                const hasDnsWarning =
                    v === 'PASS' &&
                    (!row.spfVerified || !row.dkimVerified || !row.dmarcVerified);
                return (
                    <Tag color={hasDnsWarning ? 'orange' : v === 'PASS' ? 'green' : 'default'}>
                        {hasDnsWarning
                            ? '验证通过 · DNS 建议完善'
                            : v === 'PASS'
                            ? '验证通过'
                            : v === 'FAIL'
                            ? '验证未通过'
                            : '尚未验证'}
                    </Tag>
                );
            },
        },
        { title: '状态', dataIndex: 'status', width: 100, render: (v: string) => <StatusTag value={v} /> },
        {
            title: '操作',
            width: 360,
            render: (_: any, r: MailAccount) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => open(r)}>
                        编辑
                    </Button>
                    <Button
                        size="small"
                        icon={<ApiOutlined />}
                        disabled={r.transportType !== 'SMTP'}
                        loading={saving}
                        onClick={() => test(r)}
                    >
                        测试连接
                    </Button>
                    <Button
                        size="small"
                        icon={<CloudSyncOutlined />}
                        disabled={r.transportType !== 'SMTP'}
                        loading={saving}
                        onClick={() => receiveNow(r)}
                    >
                        立即收件
                    </Button>
                    <Button
                        size="small"
                        type="primary"
                        disabled={r.transportType !== 'SMTP'}
                        loading={saving}
                        onClick={() => activate(r)}
                    >
                        验证并启用
                    </Button>
                </Space>
            ),
        },
    ];
    return (
        <div className="v2-page mail-page">
            <PageHeader
                eyebrow="系统管理 · 批量邮件"
                title="邮箱账号"
                extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => open()}>
                        新增邮箱账号
                    </Button>
                }
            />
            <Card
                className="v2-panel"
                bordered={false}
                extra={
                    <Button icon={<ReloadOutlined />} onClick={() => load()}>
                        刷新
                    </Button>
                }
            >
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={data.items}
                    loading={loading}
                    scroll={{ x: 1000 }}
                    pagination={{
                        current: data.pageNo,
                        pageSize: data.pageSize,
                        total: data.total,
                        showSizeChanger: true,
                        pageSizeOptions: MAIL_PAGE_SIZE_OPTIONS,
                        showTotal: (total) => `共 ${total} 个邮箱账号`,
                        onChange: (pageNo, pageSize) => {
                            rememberMailPageSize('accounts', pageSize);
                            load(pageNo, pageSize);
                        },
                    }}
                />
            </Card>
            <Modal
                title={editing ? '编辑邮箱账号' : '新增邮箱账号'}
                visible={visible}
                onOk={save}
                confirmLoading={saving}
                onCancel={() => setVisible(false)}
                width={900}
                okText="保存"
            >
                <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={(changed) => {
                        if (changed.transportType) setTransport(changed.transportType);
                        if (changed.fromAddress && !form.getFieldValue('imapUsername')) {
                            form.setFieldsValue({ imapUsername: changed.fromAddress });
                        }
                    }}
                >
                    <Divider orientation="left">基本信息</Divider>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="accountCode"
                                label="账号编码"
                                rules={[{ required: true }]}
                            >
                                <Input placeholder="例如 synray-marketing" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="transportType"
                                label="出站通道"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Select.Option value="SMTP">SMTP</Select.Option>
                                    <Select.Option value="PROVIDER_API">服务商 API</Select.Option>
                                    <Select.Option value="FAKE">模拟通道（仅开发）</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="senderDisplayName"
                                label="发件人显示名"
                                rules={[{ required: true }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="fromAddress"
                                label="发件邮箱"
                                rules={[{ required: true }, { type: 'email' }]}
                                extra="请填写完整邮箱地址；Reply-To 将自动使用该地址"
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    {transport === 'SMTP' && (
                        <>
                            <Divider orientation="left">发件配置（SMTP）</Divider>
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item
                                        name="smtpHost"
                                        label="SMTP 主机"
                                        rules={[{ required: true }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="smtpPort"
                                        label="端口"
                                        rules={[{ required: true }]}
                                    >
                                        <InputNumber
                                            min={1}
                                            max={65535}
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="smtpSecurity"
                                        label="加密"
                                        rules={[{ required: true }]}
                                    >
                                        <Select>
                                            <Select.Option value="SSL_TLS">SSL/TLS</Select.Option>
                                            <Select.Option value="STARTTLS">STARTTLS</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item
                                        name="smtpUsername"
                                        label="SMTP 用户名"
                                        rules={[{ required: true }]}
                                    >
                                        <Input placeholder="通常为完整邮箱地址" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="smtpPassword"
                                        label="SMTP 密码/授权码"
                                        extra="仅在保存时提交，服务端加密后写入数据库，页面和接口不会回显"
                                        rules={[
                                            {
                                                required:
                                                    !editing || !editing.credentialConfigured,
                                                message: '请输入 SMTP 密码或授权码',
                                            },
                                        ]}
                                    >
                                        <Input.Password
                                            maxLength={320}
                                            autoComplete="new-password"
                                            placeholder={
                                                editing
                                                    ? '留空表示不修改现有密码'
                                                    : '请输入密码或授权码'
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider orientation="left">收件配置（IMAP）</Divider>
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item
                                        name="imapHost"
                                        label="IMAP 主机"
                                        rules={[{ required: true, message: '请输入 IMAP 主机' }]}
                                    >
                                        <Input placeholder="例如 imap.example.com" />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="imapPort"
                                        label="IMAP 端口"
                                        rules={[{ required: true }]}
                                    >
                                        <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item
                                        name="imapSecurity"
                                        label="IMAP 加密"
                                        rules={[{ required: true }]}
                                    >
                                        <Select>
                                            <Select.Option value="SSL_TLS">SSL/TLS</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item
                                        name="imapUsername"
                                        label="IMAP 用户名"
                                        rules={[{ required: true, message: '请输入 IMAP 用户名' }]}
                                    >
                                        <Input placeholder="通常为完整邮箱地址" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="imapPassword"
                                        label="IMAP 密码/授权码"
                                        extra="可与 SMTP 授权码不同；保存后不回显"
                                        rules={[
                                            {
                                                required:
                                                    !inboundMonitor ||
                                                    !inboundMonitor.credentialConfigured,
                                                message: '请输入 IMAP 密码或授权码',
                                            },
                                        ]}
                                    >
                                        <Input.Password
                                            maxLength={320}
                                            autoComplete="new-password"
                                            placeholder={
                                                inboundMonitor?.credentialConfigured
                                                    ? '留空表示不修改现有授权码'
                                                    : '请输入 IMAP 密码或授权码'
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={12}>
                                <Col span={8}>
                                    <Form.Item
                                        name="imapFolderRef"
                                        label="收件箱目录"
                                        rules={[{ required: true }]}
                                    >
                                        <Input placeholder="INBOX" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="imapPollIntervalSeconds"
                                        label="轮询间隔（秒）"
                                        rules={[{ required: true }]}
                                    >
                                        <InputNumber min={30} max={86400} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="imapStatus"
                                        label="收件监控状态"
                                        rules={[{ required: true }]}
                                    >
                                        <Select>
                                            <Select.Option value="ACTIVE">启用</Select.Option>
                                            <Select.Option value="DISABLED">停用</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item
                                        name="imapMaxMessagesPerPoll"
                                        label="单次最多同步邮件数"
                                        rules={[{ required: true }]}
                                    >
                                        <InputNumber min={1} max={5000} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="imapInitialLookbackDays"
                                        label="首次回溯天数"
                                        rules={[{ required: true }]}
                                    >
                                        <InputNumber min={1} max={365} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                    {transport !== 'SMTP' && (
                        <>
                            <Divider orientation="left">通道配置</Divider>
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item
                                        name="smtpPassword"
                                        label="通道凭据"
                                        extra="仅在保存时提交，服务端加密后写入数据库且不会回显"
                                        rules={[
                                            {
                                                required:
                                                    !editing || !editing.credentialConfigured,
                                                message: '请输入通道凭据',
                                            },
                                        ]}
                                    >
                                        <Input.Password
                                            maxLength={320}
                                            autoComplete="new-password"
                                            placeholder={
                                                editing
                                                    ? '留空表示不修改现有凭据'
                                                    : '请输入通道凭据'
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                    <Divider orientation="left">域名认证</Divider>
                    <Row gutter={12}>
                        <Col span={8}>
                            <Form.Item
                                name="sendingDomain"
                                label="发送域名"
                                rules={[{ required: true }]}
                            >
                                <Input placeholder="synrayconsulting.com" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="dkimSelector"
                                label="DKIM Selector"
                                extra="可选；填写后将作为启用前的严格校验项"
                            >
                                <Input placeholder="31944" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="expectedSpfInclude"
                                label="SPF include"
                                extra="可选；不填写时自动检查 SPF，填写后严格匹配 include"
                            >
                                <Input placeholder="zcsend.net.cn" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider orientation="left">发送策略</Divider>
                    <Row gutter={12}>
                        <Col span={8}>
                            <Form.Item
                                name="dailyLimit"
                                label="每日上限"
                                rules={[{ required: true }]}
                            >
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="ratePerMinute"
                                label="每分钟上限"
                                rules={[{ required: true }]}
                            >
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="maxAdditionalRetries"
                                label="失败后自动重试次数"
                                extra="不含首次发送；默认 3 次表示最多尝试 4 次"
                                rules={[{ required: true }]}
                            >
                                <InputNumber min={0} max={10} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="quotaTimezone"
                                label="额度时区"
                                rules={[{ required: true }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="status" label="状态" rules={[{ required: true }]}>
                                <Select>
                                    {['INACTIVE', 'WARMING', 'PAUSED', 'DISABLED'].map((v) => (
                                        <Select.Option key={v} value={v}>
                                            {statusLabel(v)}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
};
export default MailAccountsPage;
